import React, { useCallback, useEffect, useState } from "react";
import NavBar from "../components/NavBar";
import API from "../api";
import { Plus, RefreshCw } from "lucide-react";
import AdminKPIs from "./admin/AdminKPIs";
import SalesKitPanel from "./admin/SalesKitPanel";
import UsersTable from "./admin/UsersTable";
import UserFormModal from "./admin/UserFormModal";

const EMPTY_FORM = { email: "", password: "", name: "", expiry_date: "", is_active: true };

function toDateInput(iso) {
  if (!iso) return "";
  return new Date(iso).toISOString().slice(0, 10);
}
function daysLeft(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - new Date()) / 86400000);
}

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/admin/users");
      setUsers(res.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const closeModal = () => { setShowCreate(false); setEditUser(null); setErr(""); setForm(EMPTY_FORM); };

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (editUser) {
        const payload = { name: form.name, expiry_date: form.expiry_date, is_active: form.is_active };
        if (form.password) payload.password = form.password;
        await API.patch(`/admin/users/${editUser.id}`, payload);
      } else {
        await API.post("/admin/users", form);
      }
      closeModal();
      await load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user and all their matches?")) return;
    await API.delete(`/admin/users/${id}`);
    await load();
  };

  const startEdit = (u) => {
    setEditUser(u);
    setForm({
      email: u.email, password: "", name: u.name || "",
      expiry_date: toDateInput(u.expiry_date), is_active: u.is_active,
    });
  };

  const stats = {
    total: users.length,
    active: users.filter(u => u.is_active && (!u.expiry_date || daysLeft(u.expiry_date) > 0)).length,
    expiring: users.filter(u => u.expiry_date && daysLeft(u.expiry_date) > 0 && daysLeft(u.expiry_date) <= 7).length,
    expired: users.filter(u => u.expiry_date && daysLeft(u.expiry_date) <= 0).length,
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <NavBar subtitle="Super Admin Panel" />
      <main className="pt-24 pb-16 max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-4xl font-extrabold uppercase tracking-tight">User Management</h1>
            <p className="text-slate-400 mt-1">Activate, extend and manage subscribers</p>
          </div>
          <div className="flex gap-3">
            <button onClick={load} data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700">
              <RefreshCw size={16} /> Refresh
            </button>
            <button onClick={() => { setForm(EMPTY_FORM); setShowCreate(true); }} data-testid="superadmin-create-user-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 font-semibold">
              <Plus size={16} /> Create User
            </button>
          </div>
        </div>

        <AdminKPIs stats={stats} />
        <SalesKitPanel />
        <UsersTable users={users} loading={loading} onEdit={startEdit} onDelete={remove} />
      </main>

      {(showCreate || editUser) && (
        <UserFormModal
          mode={editUser ? "edit" : "create"}
          form={form} setForm={setForm}
          err={err} busy={busy}
          onClose={closeModal} onSubmit={submit}
        />
      )}
    </div>
  );
}
